import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/BaseComponents/Card';
import './CompleteProfile.scss';
import CompleteProfileForm from '../../components/Members/CompleteProfileForm';
import CheckIcon from '../../assets/icons/color-check.webp';
import { validateToken } from '../../api/pharma';
import { categorizeError, ErrorInfo } from '../../utils/errorHandler';
import ErrorDisplay from '../../components/BaseComponents/ErrorDisplay';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';

export interface StepProps {
  number: number;
  name: string;
}

const CompleteProfile = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState<StepProps>({
    number: 1,
    name: 'Basics'
  });
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [errorInfo, setErrorInfo] = React.useState<ErrorInfo | null>(null);
  const [isTokenValidating, setIsTokenValidating] = React.useState<boolean>(true);
  const [isTokenValid, setIsTokenValid] = React.useState<boolean>(false);
  const paymentChecked = React.useRef(false);
  
  const urlParams = new URLSearchParams(window.location.search);
  const hasPaymentParams = !!(urlParams.get('payment_intent') && urlParams.get('payment_intent_client_secret'));
  const [isCheckingPayment, setIsCheckingPayment] = React.useState<boolean>(hasPaymentParams);

  const getTokenFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  };

  useEffect(() => {
    const validateTokenOnLoad = async () => {
      const token = getTokenFromURL();
      if (token) {
        sessionStorage.setItem('temp_token', token);
        
        try {
          const response = await validateToken(token);
          
          if (response?.status !== 'success') {
            const errorInfo = categorizeError({ 
              response: { 
                status: 403, 
                data: { detail: "You don't have access to this form." } 
              } 
            });
            setErrorInfo(errorInfo);
            setIsTokenValid(false);
          } else {
            setErrorInfo(null);
            setIsTokenValid(true);
          }
        } catch (error: any) {
          const errorInfo = categorizeError(error);
          setErrorInfo(errorInfo);
          setIsTokenValid(false);
        } finally {
          setIsTokenValidating(false);
        }
      } else {
        const errorInfo = categorizeError({ 
          response: { 
            status: 403, 
            data: { detail: "You don't have access to this form." } 
          } 
        });
        setErrorInfo(errorInfo);
        setIsTokenValid(false);
        setIsTokenValidating(false);
      }
    };

    validateTokenOnLoad();
  }, []);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentIntentParam = urlParams.get('payment_intent');
      const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');
      const token = urlParams.get('token');
      
      if (paymentIntentParam && paymentIntentClientSecret && token) {
        if (paymentChecked.current) {
          return;
        }
        paymentChecked.current = true;
        try {
          const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
          if (!stripe) {
            setIsCheckingPayment(false);
            return;
          }

          const { paymentIntent } = await stripe.retrievePaymentIntent(paymentIntentClientSecret);
          
          if (!paymentIntent) {
            setIsCheckingPayment(false);
            return;
          }

          if (paymentIntent.status !== 'succeeded') {
            let errorMessage = 'Payment was not successful. Please try again.';
            if (paymentIntent.status === 'canceled') {
              errorMessage = 'Payment was canceled. Please try again.';
            } else if (paymentIntent.status === 'requires_payment_method') {
              errorMessage = 'Payment failed. Please try a different payment method.';
            }
            toast.error(errorMessage);
            navigate(`/complete-subscription?token=${token}`);
            return;
          }

          window.history.replaceState({}, document.title, window.location.pathname + `?token=${token}`);
          setIsCheckingPayment(false);
        } catch (error) {
          console.error('Error verifying payment:', error);
          setIsCheckingPayment(false);
        }
      }
    };

    checkPaymentStatus();
  }, [navigate]);

  return (
    <div className='complete-profile-container'>
      {(isTokenValidating || isCheckingPayment) ? (
        <div className='round-loader'>
          <div className='spinner'></div>
        </div>
      ) : errorInfo ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '40px 20px' }}>
          <ErrorDisplay errorInfo={errorInfo} />
        </div>
      ) : isTokenValid ? (
        <Card
          backgroundColor='#F7F9FC'
          padding={'0'}
          style={{ maxWidth: 'calc(100% - 16px)' }}
        >
          {!isSubmitted ? (
            <div className='complete-profile-card-container'>
              <div className='gradient-background'></div>
              <CompleteProfileForm
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                setIsSubmitted={setIsSubmitted}
                onError={setErrorInfo}
              />
            </div>
          ) : (
            <div className='success-complete-profile'>
              <div className='gradient-background'></div>
              <img src={CheckIcon} className='check-icon' alt='check' />
              <div className='header-title'>
                Thank you for filling out the form!{' '}
              </div>
              <div>
                We now know more about you and look forward to empowering your
                health!
              </div>
              <div>
                Please schedule your initial doctor visit using the link below.
              </div>
              <div className='btn-wrapper'>
                <a
                  href='https://apps.apple.com/us/app/elevate-health/id6741478631'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='appstore'
                >
                  <img
                    src='https://elevate-olearis.s3.eu-north-1.amazonaws.com/static/Badges.png'
                    alt='appstore'
                  />
                </a>
                <a
                  href='https://play.google.com/store/apps/details?id=me.elevatehealth.app'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='play'
                >
                  <img
                    src='https://elevate-olearis.s3.eu-north-1.amazonaws.com/static/Badges (1).png'
                    alt='playmarket'
                  />
                </a>
              </div>
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
};

export default CompleteProfile;
